import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { brl, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckCircle2, Loader2, ShieldCheck, PenLine, FileDown, AlertTriangle, Handshake } from "lucide-react";
import { toast } from "sonner";
import logoDark from "@/assets/logo-dark.png";
import { FREQ } from "@/lib/status";

export default function PublicSignAcordo() {
  const { token } = useParams();
  const [a, setA] = useState(null);
  const [error, setError] = useState("");
  const [nome, setNome] = useState("");
  const [aceite, setAceite] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/public/acordos/${token}/pdf`;

  useEffect(() => {
    api.get(`/public/acordos/${token}`)
      .then(({ data }) => { setA(data); setNome(data.cliente_nome || ""); })
      .catch((e) => setError(e.response?.status === 404 ? "Acordo não encontrado ou link inválido." : e.response?.status === 410 ? (e.response?.data?.detail || "Este acordo foi removido pela empresa.") : "Não foi possível carregar o acordo agora. Verifique sua conexão e tente novamente."));
  }, [token]);

  const sign = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/public/acordos/${token}/sign`, { nome_confirmado: nome, aceite });
      setDone(data); window.scrollTo(0, 0);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao assinar"); }
    finally { setLoading(false); }
  };

  if (error) return <Center><AlertTriangle className="w-12 h-12 text-red-400 mb-4" /><p className="text-slate-300">{error}</p></Center>;
  if (!a) return <Center><Loader2 className="w-8 h-8 animate-spin gold-text" /></Center>;

  if (done || a.assinatura)
    return (
      <Center>
        <div className="w-20 h-20 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mb-6"><CheckCircle2 className="w-10 h-10 text-emerald-400" /></div>
        <h1 className="text-white text-2xl font-bold" data-testid="acordo-sign-success">Termo de acordo assinado!</h1>
        <p className="text-slate-400 mt-2 text-center max-w-sm">O termo de renegociação do contrato {a.contrato_numero} foi assinado digitalmente{done?.ip ? ` (IP ${done.ip})` : ""}. Guarde uma via em PDF.</p>
        <a href={pdfUrl} target="_blank" rel="noreferrer" className="mt-6">
          <Button className="bg-gold hover:bg-[hsl(var(--gold-hover))] text-black gap-2" data-testid="acordo-download-pdf"><FileDown className="w-4 h-4" /> Baixar termo (PDF)</Button>
        </a>
      </Center>
    );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-acordo-page">
      <header className="bg-onyx py-3 flex justify-center sticky top-0 z-10"><img src={logoDark} alt="TRT" className="h-12" /></header>
      <div className="max-w-2xl mx-auto p-4 md:p-8 space-y-5">
        <div className="text-center">
          <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight flex items-center justify-center gap-2"><Handshake className="w-7 h-7 gold-text" /> Termo de Renegociação</h1>
          <p className="text-slate-500 mt-1">Aditivo ao contrato nº {a.contrato_numero}</p>
        </div>
        <div className="trt-card p-6 space-y-3 text-sm">
          <Row l="Devedor (você)" v={a.cliente_nome} />
          <Row l="CPF" v={a.cpf} />
          <Row l="Credor" v={a.company?.razao_social} />
          <Row l="Parcelas renegociadas" v={`nº ${(a.parcelas_antigas || []).join(", ")}`} />
          <div className="grid grid-cols-2 gap-3 pt-2 border-t">
            <Box l="Saldo devido" v={brl(a.saldo_renegociado)} />
            <Box l="Encargos" v={brl(a.acrescimo)} />
            <Box l="Total do acordo" v={brl(a.total)} gold />
            <Box l="Novo plano" v={`${a.num_parcelas}x • ${FREQ[a.frequencia] || a.frequencia}`} />
            <Box l="1º vencimento" v={fmtDate(a.primeiro_vencimento)} />
            <Box l="Último vencimento" v={fmtDate(a.parcelas?.[a.parcelas.length - 1]?.vencimento)} />
          </div>
          <div className="pt-2 border-t">
            <div className="text-xs text-slate-400 mb-1">Cronograma</div>
            <div className="divide-y rounded-lg border" data-testid="acordo-cronograma">
              {(a.parcelas || []).map((p) => (
                <div key={p.id} className="flex justify-between px-3 py-1.5"><span>Parcela #{p.numero} • {fmtDate(p.vencimento)}</span><b className="mono">{brl(p.valor)}</b></div>
              ))}
            </div>
          </div>
          <a href={pdfUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 gold-text text-sm hover:underline pt-2" data-testid="acordo-view-pdf"><FileDown className="w-4 h-4" /> Ler termo completo (PDF)</a>
        </div>
        <div className="trt-card p-6 space-y-4">
          <div><Label className="text-sm">Confirme seu nome completo</Label>
            <Input value={nome} onChange={(e) => setNome(e.target.value)} className="mt-1.5" data-testid="acordo-nome-input" /></div>
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox checked={aceite} onCheckedChange={(v) => setAceite(!!v)} data-testid="acordo-aceite" className="mt-0.5" />
            <span className="text-sm text-slate-600">Reconheço a dívida descrita acima e concordo com o novo plano de pagamento, ciente de que o não pagamento implica vencimento antecipado do saldo.</span>
          </label>
          <Button onClick={sign} disabled={loading || !aceite} className="w-full bg-gold hover:bg-[hsl(var(--gold-hover))] text-black h-12 font-bold gap-2" data-testid="acordo-sign-btn">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><PenLine className="w-5 h-5" /> Assinar termo de acordo</>}
          </Button>
          <div className="flex items-center justify-center gap-2 text-slate-400 text-xs"><ShieldCheck className="w-4 h-4" /> Sua assinatura será registrada com data, hora e IP.</div>
        </div>
      </div>
    </div>
  );
}

const Center = ({ children }) => <div className="min-h-screen bg-onyx flex flex-col items-center justify-center p-6">{children}</div>;
const Row = ({ l, v }) => <div className="flex justify-between"><span className="text-slate-500">{l}</span><span className="font-medium">{v || "-"}</span></div>;
const Box = ({ l, v, gold }) => <div><div className="text-xs text-slate-400">{l}</div><div className={`font-bold mono ${gold ? "gold-text" : ""}`}>{v}</div></div>;
